import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Loader2, BellOff, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

function fromBase64Url(value) {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const bin = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export function PhonePush() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((s) => {
        setSub(s);
        if (s) api.post("/push/subscribe", s.toJSON()).catch(() => {});
      })
      .catch(() => {});
  }, []);

  if (!supported()) {
    const msg = isIOS() && !isStandalone()
      ? "Pe iPhone: apasă Partajează → „Adaugă pe ecranul principal”, deschide aplicația de acolo și activează alertele."
      : "Browserul acesta nu suportă alerte pe telefon. Folosește Chrome pe Android sau aplicația instalată pe iPhone.";
    return <p className="border-b border-white/10 px-4 py-2.5 text-xs text-slate-400" data-testid="push-unsupported">{msg}</p>;
  }

  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Permisiunea pentru notificări nu a fost acordată.");
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const s = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(data.publicKey) }));
      await api.post("/push/subscribe", s.toJSON());
      setSub(s);
      toast.success("Alertele pe acest dispozitiv sunt active");
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
    setSub(null);
    toast.success("Alertele pe acest dispozitiv au fost oprite");
  };
  const test = async () => {
    const { data } = await api.post("/push/test");
    toast.info(data.sent ? "Notificare de test trimisă" : "Niciun dispozitiv activ");
  };

  if (!sub) {
    return (
      <button type="button" onClick={enable} disabled={busy} data-testid="enable-phone-push-btn" className="flex w-full items-center gap-2 border-b border-white/10 bg-red-500/10 px-4 py-2.5 text-left text-xs text-red-200 hover:bg-red-500/20">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Primește problemele urgente pe acest telefon / dispozitiv
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 border-b border-white/10 bg-emerald-500/10 px-4 py-2.5 text-xs text-emerald-300" data-testid="phone-push-active">
      <Smartphone className="h-4 w-4" /> <span className="flex-1">Alerte urgente active aici</span>
      <button type="button" onClick={test} data-testid="test-phone-push-btn" className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 px-2 py-1 hover:bg-emerald-500/10"><Send className="h-3 w-3" /> Test</button>
      <button type="button" onClick={disable} data-testid="disable-phone-push-btn" title="Oprește" className="rounded-full p-1 hover:bg-white/5"><BellOff className="h-3.5 w-3.5" /></button>
    </div>
  );
}
